import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Home", id: "hero" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Contact", id: "contact" },
  { label: "Bar Magazine", href: "https://barmagazine.com/", external: true },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-void/70 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        {/* Simple text logo */}
        <a
          href="#hero"
          data-scroll-to="hero"
          data-testid="nav-logo"
          className="text-xl md:text-2xl font-display tracking-tight text-gold hover:opacity-80 transition-opacity"
        >
          Mixing Dreams <span className="text-gold">Ltd</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-link-bar-magazine"
                className="group relative text-xs uppercase tracking-[0.2em] text-coral hover:text-coral/70 transition-colors duration-300"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-coral transition-all duration-300 group-hover:w-full" />
              </a>
            ) : (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-scroll-to={l.id}
                data-testid={`nav-link-${l.id}`}
                className="group relative text-xs uppercase tracking-[0.2em] text-gold hover:text-gold/70 transition-colors duration-300"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </a>
            )
          )}
        </nav>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-ink"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-void/95 backdrop-blur-xl border-t border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.external ? (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-link-bar-magazine"
                    className="text-sm uppercase tracking-[0.2em] text-coral hover:text-coral/70 transition-colors"
                  >
                    {l.label}
                  </a>
                ) : (
                  <a
                    key={l.id}
                    href={`#${l.id}`}
                    data-scroll-to={l.id}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="text-sm uppercase tracking-[0.2em] text-gold hover:text-gold/70 transition-colors"
                  >
                    {l.label}
                  </a>
                )
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
